import React, { createContext, useState } from 'react';

export const AuthContext = createContext('');

// Keys stored in sessionStorage
const STORAGE_KEYS = [
  'token',
  'user_id',
  'login_name',
  'login_fname',
  'login_lname',
  'is_admin',
  'role_name',
  'department',
  'permissions',
];

/** Helper: write key-value pairs into a Storage object */
const setStorage = (storage, data) => {
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      storage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value);
    }
  });
};

/** Helper: remove all known keys from a Storage object */
const clearStorage = (storage) => {
  STORAGE_KEYS.forEach((key) => storage.removeItem(key));
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!sessionStorage.getItem('token')
  );

  /**
   * Login — called after POST /api/auth/login/ succeeds.
   * @param {object} loginData  The response payload from the backend
   */
  const login = (loginData) => {
    try {
      const mapped = {
        token: loginData.token,
        user_id: loginData.user_id,
        login_name: loginData.login_name || loginData.username,
        login_fname: loginData.login_fname || loginData.first_name,
        login_lname: loginData.login_lname || loginData.last_name,
        is_admin: loginData.is_admin ? 'true' : '',
        role_name: loginData.role_name,
        department: loginData.department,
        permissions: loginData.permissions,
      };

      setStorage(sessionStorage, mapped);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Error during login:', error);
    }
  };

  /**
   * Logout — clears session and resets state.
   */
  const logout = () => {
    clearStorage(sessionStorage);
    setIsAuthenticated(false);
  };

  /**
   * Check whether the current user has a specific permission.
   * Admin role bypasses all checks.
   */
  const hasPermission = (module, action = 'view') => {
    if (sessionStorage.getItem('is_admin') === 'true') return true;
    try {
      const perms = JSON.parse(sessionStorage.getItem('permissions') || '{}');
      const key = action.startsWith('can_') ? action : `can_${action}`;
      return perms?.[module]?.[key] ?? false;
    } catch {
      return false;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        login,
        logout,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
